export type ListKeyboardCommand =
  | "focus-search"
  | "escape"
  | "row-next"
  | "row-previous"
  | "row-activate";

export type ListKeyboardEventLike = {
  key: string;
  altKey?: boolean;
  ctrlKey?: boolean;
  metaKey?: boolean;
  target?: EventTarget | null;
};

export type ListKeyboardCapabilities = {
  search?: boolean;
  escape?: boolean;
  rows?: boolean;
};

export const isEditableListKeyboardTarget = (target?: EventTarget | null): boolean => {
  const element = target as {
    tagName?: string;
    isContentEditable?: boolean;
    closest?: (selector: string) => unknown;
  } | null;

  if (!element) {
    return false;
  }

  const tagName = `${element.tagName || ""}`.toUpperCase();
  if (["INPUT", "TEXTAREA", "SELECT"].includes(tagName) || element.isContentEditable) {
    return true;
  }

  return Boolean(element.closest?.("[contenteditable='true']"));
};

export const resolveListKeyboardCommand = (
  event: ListKeyboardEventLike,
  capabilities: ListKeyboardCapabilities = {},
): ListKeyboardCommand | null => {
  const key = event.key.toLowerCase();
  const commandModifier = Boolean(event.metaKey || event.ctrlKey);

  if (capabilities.search !== false && commandModifier && !event.altKey && key === "k") {
    return "focus-search";
  }

  if (capabilities.escape !== false && event.key === "Escape") {
    return "escape";
  }

  if (capabilities.rows === false || isEditableListKeyboardTarget(event.target)) {
    return null;
  }

  if (event.key === "ArrowDown") {
    return "row-next";
  }
  if (event.key === "ArrowUp") {
    return "row-previous";
  }
  if (event.key === "Enter") {
    return "row-activate";
  }

  return null;
};

export const nextListRowIndex = (
  currentIndex: number,
  rowCount: number,
  direction: "next" | "previous",
): number | null => {
  if (rowCount <= 0 || currentIndex < 0 || currentIndex >= rowCount) {
    return null;
  }

  if (direction === "next") {
    return Math.min(currentIndex + 1, rowCount - 1);
  }

  return Math.max(currentIndex - 1, 0);
};
